import React, { useEffect, useRef } from 'react';
import { ArrowUpIcon, CameraIcon, MapPinIcon, MicIcon, SparklesIcon } from 'lucide-react';
interface PromptBoxProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  /** Bouton photo affiche uniquement si un gestionnaire est fourni. */
  onAddPhoto?: () => void;
  /** `hero` is the single-line pill shown before the conversation starts. */
  variant: 'hero' | 'chat';
  placeholder: string;
  busy?: boolean;
  photoAttached?: boolean;
  autoFocus?: boolean;
}
const toolButton = 'grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
export function PromptBox({
  value,
  onChange,
  onSubmit,
  onAddPhoto,
  variant,
  placeholder,
  busy = false,
  photoAttached = false,
  autoFocus = false
}: PromptBoxProps) {
  const areaRef = useRef<HTMLTextAreaElement | null>(null);
  const inputId = variant === 'hero' ? 'request-input' : 'request-reply';
  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 168)}px`;
  }, [value, variant]);
  useEffect(() => {
    if (!autoFocus) return;
    const timer = window.setTimeout(() => areaRef.current?.focus(), 320);
    return () => window.clearTimeout(timer);
  }, [autoFocus]);
  const send = () => {
    if (busy) return;
    onSubmit();
  };
  if (variant === 'hero') {
    return <form onSubmit={event => {
      event.preventDefault();
      send();
    }} className="flex items-center gap-2 rounded-full border border-slate-200 bg-white py-2.5 pl-5 pr-2.5 shadow-lg shadow-ville-navy/5 transition-colors duration-150 ease-ville focus-within:border-ville-indigo" data-id="element-818" data-mp="1vstkj8ocovm4">
        <SparklesIcon className="h-5 w-5 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-819" data-mp="2jifla0kdfiw8" />
        <label htmlFor={inputId} className="sr-only" data-id="element-820" data-mp="2en8olcpiregt">
          What would you like to report?
        </label>
        <input id={inputId} type="text" value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} className="min-w-0 flex-1 border-0 bg-transparent py-2.5 text-base text-ville-navy placeholder:text-slate-400 focus:outline-none sm:text-lg" data-id="element-821" data-mp="1c5wq3w3lw2gs" />
        {onAddPhoto && <button type="button" onClick={onAddPhoto} aria-label="Add a photo" className={toolButton} data-id="element-822" data-mp="1q0pap8ycs8u8">
            <CameraIcon className="h-5 w-5" aria-hidden="true" data-id="element-823" data-mp="1uy93ddvsso5p" />
          </button>}
        <button type="button" aria-label="Use my location" className={`hidden sm:grid ${toolButton}`} data-id="element-824" data-mp="1zwamtzx4yphp">
          <MapPinIcon className="h-5 w-5" aria-hidden="true" data-id="element-825" data-mp="24x6khvqw9feo" />
        </button>
        <button type="button" aria-label="Dictate the description" className={toolButton} data-id="element-826" data-mp="28x8r8jbhkz0t">
          <MicIcon className="h-5 w-5" aria-hidden="true" data-id="element-827" data-mp="ajfhvg6zfvsq" />
        </button>
        <button type="submit" aria-label="Send the description to ATNA" className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-ville-lime text-ville-navy transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-828" data-mp="ejzaq7kiwd92">
          <ArrowUpIcon className="h-5 w-5" aria-hidden="true" data-id="element-829" data-mp="3th44y1jaq80d" />
        </button>
      </form>;
  }
  return <form onSubmit={event => {
    event.preventDefault();
    send();
  }} className="rounded-[24px] border border-slate-200 bg-white px-4 pb-3 pt-3.5 transition-colors duration-150 ease-ville focus-within:border-ville-indigo" data-id="element-830" data-mp="3pg0l73y4nrtv">
      <label htmlFor={inputId} className="sr-only" data-id="element-831" data-mp="kyeltl269joj">
        Reply to ATNA
      </label>
      <textarea id={inputId} ref={areaRef} rows={1} value={value} onChange={event => onChange(event.target.value)} onKeyDown={event => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        send();
      }
    }} placeholder={placeholder} className="block max-h-[168px] w-full resize-none border-0 bg-transparent px-1 text-base leading-relaxed text-ville-navy placeholder:text-slate-400 focus:outline-none sm:text-[17px]" data-id="element-832" data-mp="gx2wzhr4h2he" />

      <div className="mt-1.5 flex items-center justify-between gap-3" data-id="element-833" data-mp="t93wwrxq5lg4">
        <div className="flex items-center gap-0.5" data-id="element-834" data-mp="fgxipbe4tojb">
          {onAddPhoto && <button type="button" onClick={onAddPhoto} aria-label="Attach a photo" aria-pressed={photoAttached} className={photoAttached ? 'grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo' : toolButton} data-id="element-835" data-mp="feks69ub65un">
              <CameraIcon className="h-5 w-5" aria-hidden="true" data-id="element-836" data-mp="y7f7x5zra1zx" />
            </button>}
          <button type="button" aria-label="Use my location" className={toolButton} data-id="element-837" data-mp="y9hsvbm1x4xs">
            <MapPinIcon className="h-5 w-5" aria-hidden="true" data-id="element-838" data-mp="138cmz9pgyfx7" />
          </button>
          <button type="button" aria-label="Dictate your reply" className={toolButton} data-id="element-839" data-mp="3ivt8uqdauzns">
            <MicIcon className="h-5 w-5" aria-hidden="true" data-id="element-840" data-mp="3nx8uoknaxa78" />
          </button>
          {photoAttached && <span className="ml-1 text-xs font-semibold text-ville-indigo" data-id="element-841" data-mp="3fe5eop4svrc3">1 photo attached</span>}
        </div>

        <button type="submit" disabled={busy || value.trim().length === 0} aria-label="Send to ATNA" className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-ville-lime text-ville-navy transition-[transform,opacity] duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 disabled:hover:translate-y-0" data-id="element-842" data-mp="3bdbw8ba4n4ql">
          <ArrowUpIcon className="h-5 w-5" aria-hidden="true" data-id="element-843" data-mp="36fk2rctu3aka" />
        </button>
      </div>
    </form>;
}